import { handleExtensionAuth } from "@/components/feature/Authenticaton/Authentication"
import { TURNUS_EXTENSION_IDS } from "@/components/feature/Authenticaton/extensionAuthHandoff"
import { useGetUsersLimits } from "@/services/api/userService"
import { useEffect, useRef, useState } from "react"
import {
  detectBrowser,
  type ExtensionStatus,
  getExtensionStatus,
} from "./extensionStatus"
import {
  confirmFirefoxExtension,
  isExtensionHandoffDone,
  isFirefoxExtensionConfirmed,
  markExtensionHandoffDone,
} from "./referralSource"

const EXTENSION_POLL_MS = 2000

// Shared by every mount: leaving Home while a handoff is pending must not
// start a second one when the user comes back.
let handoffInFlight: Promise<boolean> | null = null

/**
 * Step status for the Ariba checklist, derived from data the app already has:
 * uploads and extension calls from the usage counters (refetched when the tab
 * regains focus), the extension from its own answer to the app.
 */
export const useAribaOnboarding = () => {
  const [browser] = useState(() => detectBrowser())
  const { data: limits, isLoading } = useGetUsersLimits()
  const [extension, setExtension] = useState<ExtensionStatus>({
    installed: false,
    authenticated: false,
  })
  const [extensionChecked, setExtensionChecked] = useState(false)
  const [firefoxConfirmed, setFirefoxConfirmed] = useState(
    isFirefoxExtensionConfirmed,
  )
  const handoffTried = useRef(false)

  const uploadedFiles = limits?.resourceUsage.files?.usage ?? 0
  const extensionCalls = limits?.resourceUsage.extensionCalls?.usage ?? 0

  const documentsDone = uploadedFiles > 0
  const aribaDone = extensionCalls > 0
  const extensionDone =
    aribaDone ||
    (browser === "firefox" ? firefoxConfirmed : extension.authenticated)

  const pollExtension =
    (browser === "chrome" || browser === "edge") && !extensionDone

  useEffect(() => {
    if (!pollExtension) return
    let cancelled = false

    const check = async () => {
      if (document.visibilityState !== "visible") return
      const status = await getExtensionStatus()
      if (cancelled) return
      setExtension(status)
      setExtensionChecked(true)

      // A freshly installed extension does not run in tabs that were already
      // open, so it cannot pick up the session itself: hand it over, once per
      // browser, at most once per visit and never twice at the same time. No
      // forced refresh: the user did not ask for this, so a failed refresh
      // must not sign them out of the app.
      if (
        status.installed &&
        !status.authenticated &&
        !handoffTried.current &&
        !handoffInFlight &&
        !isExtensionHandoffDone()
      ) {
        handoffTried.current = true
        handoffInFlight = handleExtensionAuth(TURNUS_EXTENSION_IDS[0], {
          forceRefresh: false,
        })
        const delivered = await handoffInFlight.finally(() => {
          handoffInFlight = null
        })
        if (delivered) markExtensionHandoffDone()
        if (delivered && !cancelled) {
          setExtension({ installed: true, authenticated: true })
        }
      }
    }

    void check()
    const interval = window.setInterval(check, EXTENSION_POLL_MS)
    return () => {
      cancelled = true
      window.clearInterval(interval)
    }
  }, [pollExtension])

  const confirmFirefox = () => {
    confirmFirefoxExtension()
    setFirefoxConfirmed(true)
  }

  return {
    isLoading,
    // True once the step states are real: usage loaded and, while the page
    // polls the extension, its first answer is in.
    statusSettled: !isLoading && (!pollExtension || extensionChecked),
    browser,
    uploadedFiles,
    extensionInstalled: extension.installed,
    documentsDone,
    extensionDone,
    aribaDone,
    confirmFirefox,
  }
}
